import React from "react";
import { IWDDesignModeProps } from "../common";
import { IContextProvider } from "../../../uxp";
import { IDataSource, IDataSourceBinding, IQueryDataSource, processSource, TargetDataStructure } from "./datasource";
import { IContextData } from "../../../hooks/useContextData";
import { useAlert, useToast } from "uxp/components";
import { getQueryBuilder } from "./source-picker-helper";
import { LucyQueryBuilderDataSourcePicker } from "./lucy-query-builder/LucyQueryBuilderSourceEditor";
import { DataSourceBrowser } from "./datasource-editor/model-action-browser";
import './SourcePickerWrapper.scss'
import { generateUUID } from "../../../util";

// source picker
interface SourcePickerProps {
    designer: IWDDesignModeProps,
    uxpContext: IContextProvider,
    selectedSource: IDataSource,
    existingSources: IDataSource[],
    onSelect: (source: IDataSource) => void,
    onRemove: (id: string) => void,
    contextData: IContextData,
    onClose: () => void,
    target: TargetDataStructure
    show: boolean
}

export const SourcePickerWrapper: React.FC<SourcePickerProps> = (props) => {

    const { designer, uxpContext, selectedSource, existingSources, onSelect, onRemove, contextData, onClose, target, show } = props

    const [hasQB, setHasQB] = React.useState(false)
    const [showQB, setShowQB] = React.useState(false)
    const [loading, setLoading] = React.useState(true)

    const alerts = useAlert()
    const toast = useToast()

    React.useEffect(() => {
        getQueryBuilderIfEnabled()
    }, [])

    async function getQueryBuilderIfEnabled() {
        setLoading(true)

        const config = localStorage.getItem('lucy_canvas_ds_picker')
        const canShowQB = (config && config === 'qb')
        setShowQB(canShowQB)

        if (!canShowQB) setLoading(false)

        const qb = await getQueryBuilder(uxpContext)
        setHasQB(!!qb)
        setLoading(false)
    }

    async function handleExternalSourcSelect(source: any) {
        try {
            let res = await processSource(designer, uxpContext, {}, source, target, existingSources, selectedSource?.id);
            if (res?.source) {
                const _sources = res?.datasources || []
                const _source = _sources.find((s: any) => s.id == (res.source as IDataSourceBinding).source)
                onSelect(_source);
                return true;
            }
            else {
                alerts.show('Unable to parse the source. something went wrong')
                return false
            }
        }
        catch (e) {
            alerts.show({ title: 'Invalid Data Source', content: 'The selected data source does not seem to be compatible with this widget. Select a different source? Or maybe a different widget?' });
            return false;
        }
    }

    async function switchToQB() {
        toast.info('Switching to Data Explorer...')
        localStorage.setItem('lucy_canvas_ds_picker', 'qb')
        setShowQB(true)
    }

    async function switchToOld() {
        toast.info('Switching to canvas data source picker...')
        localStorage.setItem('lucy_canvas_ds_picker', 'canvas')
        setShowQB(false)
    }

    function renderQBNotification() {
        if (!hasQB) return null

        return <div className="wd-ds-picker-notification-contianer" onClick={switchToQB}>
            <strong>Data Explorer</strong>&nbsp;<span>Beta</span>&nbsp;version is now available. Click here to try it.  You can always switch back if needed.
        </div>
    }

    function renderFooterNote() {
        return <div className="qb-ds-picker-notification-contianer">
            <span onClick={switchToOld}>
                Click here to switch to canvas data source picker
            </span>
        </div >
    }


    function renderExternalSourceBrowser() {
        return designer?.sourceBrowser(uxpContext, null, onClose, handleExternalSourcSelect, selectedSource?.__source__ || null, contextData)
    }

    async function saveQueryChanges(uxpContext: IContextProvider, source: IQueryDataSource): Promise<{ status: 'done' | 'error', message: string, queryId?: string }> {
        try {
            const service = `Query:Create`
            const params: any = {
                Name: source.name,
                Description: source?.description || '',
                ID: generateUUID(),
                Configuration: JSON.stringify(source)
            }

            const res = await uxpContext.executeService('Lucy', service, params, { json: true })
            if (!!res?.[0]?.['Key']) {
                return { status: 'done', message: 'Query saved!', queryId: params.ID }
            }
            console.log('Invalid response for saving query', res)
            return { status: 'error', message: 'Invalid response received' }
        }
        catch (e) {
            console.log('Error saving query', e)
            return { status: 'error', message: e }
        }
    }


    if (!show || loading) return null
    if (designer?.sourceBrowser) {
        return renderExternalSourceBrowser()
    }

    if (hasQB && showQB) {
        return <LucyQueryBuilderDataSourcePicker
            show={true}
            selectedSource={selectedSource}
            onSelect={handleExternalSourcSelect}
            onRemove={onRemove}
            context={uxpContext}
            contextData={contextData}
            onClose={onClose}
            renderFooterNote={renderFooterNote()}
            onSave={s => saveQueryChanges(uxpContext, s as IQueryDataSource)}
            canSelectSavedQuerries={true}
        />
    }

    return <DataSourceBrowser
        selectedSource={selectedSource}
        existingSources={existingSources}
        onSelected={onSelect}
        onRemove={onRemove}
        context={uxpContext}
        contextData={contextData}
        onClose={onClose}
        renderNotificationBar={renderQBNotification()}
    />
}